import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import { FEVER_COMBO, FEVER_SECONDS } from "./progression";
import { createNebula, createStars, drawBeatFlash, drawCoverDisc, drawGlow, drawStars, drawVinyl, glowText, withAlpha } from "./render";
import { createFeedbackQueue, createTally, pointerInput, type FrameInput, type GameEngine, type GameEnv, type GameProbe, type GameStatus } from "./types";

type FormKind = "vee" | "line" | "sweep" | "spiral" | "divers";
type EnemyKind = "grunt" | "gunner" | "diver" | "minion";
type ShotStyle = "none" | "aimed" | "spread" | "down";
type PowerKind = "triple" | "rapid" | "shield" | "bomb";
type Stage = "waves" | "boss" | "clear";

interface Enemy {
  kind: EnemyKind;
  form: FormKind;
  group: number;
  age: number;
  delay: number;
  // Path anchors as fractions of the stage: where it comes from, where it
  // holds, how long each part takes, and how it sways while holding.
  ex: number;
  ey: number;
  hx: number;
  hy: number;
  tIn: number;
  tHold: number;
  sway: number;
  swayRate: number;
  phase: number;
  dir: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  size: number;
  color: string;
  fireEvery: number;
  fireOffset: number;
  style: ShotStyle;
  charge: number;
  // 0 flying its path, 1 flashing a warning, 2 diving at the locked column.
  dive: 0 | 1 | 2;
  diveT: number;
  diveX: number;
  diveFromX: number;
  diveFromY: number;
  hitFlash: number;
  spin: number;
  gone: boolean;
}

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  grazed: boolean;
}

interface Shot {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface PowerUp {
  x: number;
  y: number;
  kind: PowerKind;
  age: number;
}

interface Boss {
  level: number;
  name: string;
  hp: number;
  maxHp: number;
  x: number;
  y: number;
  t: number;
  enter: number;
  beats: number;
  spiral: number;
  burstLeft: number;
  burstTimer: number;
  halfTimer: number;
  hitFlash: number;
  hitAt: number;
}

interface Group {
  left: number;
  escaped: boolean;
}

export interface BassBlasterDebug {
  level: number;
  stage: Stage;
  boss: { hp: number; maxHp: number; name: string } | null;
  bossesSpawned: number;
  bossesDefeated: number;
  closestBulletSpawn: number;
  bossLog: string[];
  formations: Record<string, number>;
  shipX: number;
  shipY: number;
  bullets: Array<{ x: number; y: number }>;
}

const MAX_LIVES = 3;
const MAX_BULLETS = 180;
const CHARGE_SECONDS = 0.42;
const DIVE_WARN_SECONDS = 0.8;
const CLEAR_SECONDS = 1.1;
const LEFT_KEYS = new Set(["ArrowLeft", "KeyA"]);
const RIGHT_KEYS = new Set(["ArrowRight", "KeyD"]);
const BOSS_NAMES = ["THE 45", "THE LONG PLAYER", "THE SUBWOOFER", "THE HEADLINER", "THE ENCORE"];
const LEVEL_NOTES = ["", "", "Divers and snake lines", "Spirals and spread shots", "Two waves at once", "Faster and denser"];
const POWER_LABEL: Record<PowerKind, string> = { triple: "T", rapid: "R", shield: "S", bomb: "B" };
const POWER_NAME: Record<PowerKind, string> = { triple: "TRIPLE SHOT", rapid: "RAPID FIRE", shield: "SHIELD", bomb: "BASS BOMB" };

// Autopilot look ahead: how far and how finely it plays the next moment out.
const PLAN_STEP = 1 / 20;
const PLAN_STEPS = 18;
const PLAN_LANES = 21;
const MAX_TRACKED = 240;

const clamp = (value: number, low: number, high: number) => (value < low ? low : value > high ? high : value);
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);

// Vertical shooter in deep space. Records fly in formations on the beat grid,
// gunners flash before they fire, divers flash and lock a column before they
// drop, and every level ends in a boss fight with three phases.
export function createBassBlaster(env: GameEnv): GameEngine & { inspect(): BassBlasterDebug } {
  const { theme, rng, art } = env;
  const difficulty = env.difficulty;
  const { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, art, env.quiet);
  const farStars = createStars(rng, 70);
  const nearStars = createStars(rng, 40);
  const nebula = createNebula(art, palette, rng);
  let width = env.width;
  let height = env.height;

  let time = 0;
  let travel = 0;
  let score = 0;
  let combo = 0;
  let lives = MAX_LIVES;
  let over = false;
  let damage = 0;
  let invulnerable = 0;
  let shield = false;
  let tripleShot = 0;
  let rapidFire = 0;
  let fever = 0;
  let lastFeverCombo = 0;
  let lastHitAt = 0;
  let fireTimer = 0;
  let shipX = width / 2;
  let targetX = width / 2;
  let shipPositionY = height * 0.86;
  let targetY = shipPositionY;
  let shipTilt = 0;
  let level = 1;
  let stage: Stage = "waves";
  let stageClock = 0;
  let beatsToForm = 1;
  let lastForm: FormKind | null = null;
  let groupId = 0;
  let dropTurn = 0;
  let beatCount = 0;
  let lastGraze = -10;
  let enemies: Enemy[] = [];
  let shots: Shot[] = [];
  let bullets: Bullet[] = [];
  let powerUps: PowerUp[] = [];
  let boss: Boss | null = null;
  let bossesSpawned = 0;
  let bossesDefeated = 0;
  let closestBulletSpawn = Infinity;
  let beatSeconds = 0.5;
  const bossLog: string[] = [];
  let beatGlow = 0;
  const groups = new Map<number, Group>();
  const tally = createTally();
  const formations: Record<string, number> = {};
  const feedbackQueue = createFeedbackQueue();
  const point = { x: 0, y: 0 };
  // Scratch buffers for the autopilot's plan, reused every frame.
  const trackX = new Float32Array(PLAN_STEPS * MAX_TRACKED);
  const trackY = new Float32Array(PLAN_STEPS * MAX_TRACKED);
  const trackR = new Float32Array(MAX_TRACKED);
  const lanes: number[] = [];
  let lastChoice = width / 2;

  const base = () => Math.min(width, height);
  const shipY = () => shipPositionY;
  const shipSize = () => Math.max(16, Math.min(26, base() * 0.045));
  const hitRadius = () => shipSize() * 0.3;
  const enemyRadius = () => Math.max(13, Math.min(26, base() * 0.04));
  const bulletRadius = () => Math.max(4.5, Math.min(7, base() * 0.0095));
  const maxSpeed = () => clamp(width * 1.2, 560, 900);
  const safeSpawnDistance = () => Math.max(shipSize() * 5, height * 0.26);
  const waveSeconds = () => 21 + level;
  const levelProgress = () => (stage === "waves" ? clamp(stageClock / waveSeconds(), 0, 1) : 1);
  // How hard the game is pushing right now. Bullet speed, fire rates and
  // formation gaps all read it, and it is what `probe().intensity` reports.
  const heat = () => difficulty * (1 + (level - 1) * 0.22) * (1 + levelProgress() * 0.12);
  const bulletSpeed = () => height * Math.min(0.46, 0.25 * heat());
  // Points grow with the kill combo and with time since the last hit, up to
  // x4 after 90 seconds clean. Any hit drops the streak back to x1.
  const streakMultiplier = () => 1 + Math.min(3, Math.floor((time - lastHitAt) / 15) * 0.5);
  const multiplier = () => (fever > 0 ? 2 : 1) * (1 + Math.min(combo, 50) * 0.04) * streakMultiplier();

  // Where an enemy is `age` seconds into its life, ignoring any dive.
  const pathAt = (e: Enemy, age: number, out: { x: number; y: number }) => {
    const a = age - e.delay;
    if (e.form === "sweep") {
      const u = e.dir > 0 ? -0.08 + e.sway * a : 1.08 - e.sway * a;
      out.x = u * width;
      out.y = (e.hy + Math.sin(a * e.swayRate + e.phase) * 0.05) * height;
      return;
    }
    if (e.form === "spiral") {
      const b = base();
      const enter = easeOut(clamp(a / e.tIn, 0, 1));
      const cy = (e.ey + (e.hy - e.ey) * enter) * height;
      let radius = b * (0.05 + 0.13 * enter);
      const out2 = a - e.tIn - e.tHold;
      if (out2 > 0) radius += out2 * out2 * height * 0.35 + out2 * height * 0.12;
      const angle = e.phase + e.dir * a * e.swayRate;
      out.x = e.hx * width + Math.cos(angle) * radius;
      out.y = cy + Math.sin(angle) * radius * 0.8;
      return;
    }
    if (a < e.tIn) {
      const p = easeOut(clamp(a / e.tIn, 0, 1));
      out.x = (e.ex + (e.hx - e.ex) * p) * width;
      out.y = (e.ey + (e.hy - e.ey) * p) * height;
      return;
    }
    const held = Math.min(a - e.tIn, e.tHold);
    const swayX = Math.sin(e.phase + held * e.swayRate) * e.sway;
    out.x = (e.hx + swayX) * width;
    out.y = (e.hy + Math.sin(held * 1.7 + e.phase) * 0.008) * height;
    const leaving = a - e.tIn - e.tHold;
    if (leaving > 0) out.y += (leaving * 0.22 + leaving * leaving * 0.3) * height;
  };

  // A diver's position `t` seconds from now, including the warning and dive.
  const predict = (e: Enemy, ahead: number, out: { x: number; y: number }) => {
    if (e.dive === 0) {
      pathAt(e, e.age + ahead, out);
      return;
    }
    let t = e.diveT + ahead;
    if (e.dive === 1) {
      if (t < DIVE_WARN_SECONDS) {
        out.x = e.x;
        out.y = e.y;
        return;
      }
      t -= DIVE_WARN_SECONDS;
      const fromX = e.x;
      const fromY = e.y;
      const p = easeOut(clamp(t / 0.35, 0, 1));
      out.x = fromX + (e.diveX * width - fromX) * p;
      out.y = fromY + height * (0.75 * t + 0.45 * t * t);
      return;
    }
    const p = easeOut(clamp(t / 0.35, 0, 1));
    out.x = e.diveFromX + (e.diveX * width - e.diveFromX) * p;
    out.y = e.diveFromY + height * (0.75 * t + 0.45 * t * t);
  };

  const onScreen = (e: Enemy) => {
    const r = enemyRadius() * e.size;
    return e.y > -r && e.y < height + r && e.x > -r && e.x < width + r;
  };

  const makeEnemy = (kind: EnemyKind, form: FormKind, group: number, overrides: Partial<Enemy>): Enemy => {
    const hp = kind === "diver" ? 2 : kind === "gunner" ? (level >= 3 ? 2 : 1) : 1;
    const enemy: Enemy = {
      kind,
      form,
      group,
      age: 0,
      delay: 0,
      ex: 0.5,
      ey: -0.12,
      hx: 0.5,
      hy: 0.2,
      tIn: 1.3,
      tHold: 4.5,
      sway: 0.03,
      swayRate: 1.1,
      phase: 0,
      dir: 1,
      x: -9999,
      y: -9999,
      hp,
      maxHp: hp,
      size: kind === "minion" ? 0.8 : kind === "diver" ? 1.05 : 1,
      color: kind === "diver" || kind === "minion" ? palette.danger : kind === "gunner" ? palette.secondary : palette.lanes[group % palette.lanes.length],
      fireEvery: 0,
      fireOffset: 0,
      style: "none",
      charge: 0,
      dive: 0,
      diveT: 0,
      diveX: 0.5,
      diveFromX: 0,
      diveFromY: 0,
      hitFlash: 0,
      spin: rng() * 6,
      gone: false,
      ...overrides,
    };
    pathAt(enemy, 0, point);
    enemy.x = point.x;
    enemy.y = point.y;
    tally.add(kind);
    return enemy;
  };

  const gunnerStyle = (): ShotStyle => (level >= 3 ? "spread" : "aimed");
  const fireEvery = () => (level >= 4 ? 2 : level >= 2 ? 3 : 4);
  const holdSeconds = () => Math.max(3.2, 4.6 - (level - 1) * 0.35);

  const spawnFormation = (kind: FormKind) => {
    groupId += 1;
    const group = groupId;
    const r = enemyRadius();
    const members: Enemy[] = [];
    formations[kind] = (formations[kind] ?? 0) + 1;
    tally.add(kind);
    // Formations take turns between two rows so a new one never lands on
    // top of one that is still holding.
    const band = group % 2 === 0 ? 0.16 : 0;
    const fit = (spacing: number) => Math.max(3, Math.floor((width - r * 2) / spacing) + 1);
    if (kind === "vee") {
      let n = Math.min(level >= 3 ? 7 : 5, fit(r * 3));
      if (n % 2 === 0) n -= 1;
      const dx = (r * 3) / width;
      const span = ((n - 1) / 2) * dx;
      const cx = clamp(0.5 + (rng() - 0.5) * 0.4, span + r / width + 0.02, 1 - span - r / width - 0.02);
      for (let i = 0; i < n; i++) {
        const slot = i - (n - 1) / 2;
        const edge = Math.abs(slot) === (n - 1) / 2;
        const gunner = slot === 0 || (level >= 2 && edge);
        members.push(
          makeEnemy(gunner ? "gunner" : "grunt", kind, group, {
            ex: cx + slot * dx,
            hx: cx + slot * dx,
            hy: 0.2 + band - Math.abs(slot) * 0.035,
            delay: Math.abs(slot) * 0.12,
            tHold: holdSeconds(),
            phase: 0,
            fireEvery: gunner ? fireEvery() : 0,
            fireOffset: Math.abs(slot),
            style: gunner ? gunnerStyle() : "none",
          }),
        );
      }
    } else if (kind === "line") {
      const n = Math.min(level >= 3 ? 8 : 6, fit(r * 2.9));
      const dx = Math.min(0.9 / Math.max(1, n - 1), (r * 3.4) / width);
      const start = 0.5 - ((n - 1) / 2) * dx;
      const hy = 0.13 + band + rng() * 0.03;
      for (let i = 0; i < n; i++) {
        const gunner = i % 3 === 1;
        members.push(
          makeEnemy(gunner ? "gunner" : "grunt", kind, group, {
            ex: start + i * dx,
            hx: start + i * dx,
            hy,
            delay: i * 0.07,
            tHold: holdSeconds() + i * 0.12,
            sway: 0.05,
            swayRate: 0.9,
            fireEvery: gunner ? fireEvery() : 0,
            fireOffset: i,
            style: gunner ? (level >= 3 ? "spread" : "down") : "none",
          }),
        );
      }
    } else if (kind === "sweep") {
      const n = level >= 3 ? 8 : 6;
      const dir = rng() < 0.5 ? 1 : -1;
      const hy = 0.14 + rng() * 0.12;
      const speed = (0.2 + level * 0.015) * Math.min(1.25, 700 / Math.max(width, 300) + 0.35);
      for (let i = 0; i < n; i++) {
        const gunner = i % 2 === 1;
        members.push(
          makeEnemy(gunner ? "gunner" : "grunt", kind, group, {
            delay: i * 0.3,
            hy,
            dir,
            sway: speed,
            swayRate: 2.2,
            phase: i * 0.5,
            fireEvery: gunner ? fireEvery() + 1 : 0,
            fireOffset: i,
            style: gunner ? "aimed" : "none",
          }),
        );
      }
    } else if (kind === "spiral") {
      const n = level >= 4 ? 8 : 6;
      const dir = rng() < 0.5 ? 1 : -1;
      const hx = 0.5 + (rng() - 0.5) * 0.2;
      for (let i = 0; i < n; i++) {
        const gunner = i % 3 === 0;
        members.push(
          makeEnemy(gunner ? "gunner" : "grunt", kind, group, {
            hx,
            hy: 0.3,
            ey: -0.1,
            tIn: 1.5,
            tHold: holdSeconds(),
            dir,
            swayRate: 1.1,
            phase: (i / n) * Math.PI * 2,
            fireEvery: gunner ? fireEvery() : 0,
            fireOffset: i,
            style: gunner ? gunnerStyle() : "none",
          }),
        );
      }
    } else {
      const n = level >= 4 ? 3 : 2;
      for (let i = 0; i < n; i++) {
        const hx = (i + 1) / (n + 1) + (rng() - 0.5) * 0.08;
        members.push(
          makeEnemy("diver", kind, group, {
            ex: hx,
            hx,
            hy: 0.1 + band + (i % 2) * 0.04,
            tIn: 1.1,
            tHold: 6,
            sway: 0.04,
            fireEvery: 4,
            fireOffset: i * 2 + 1,
          }),
        );
      }
    }
    groups.set(group, { left: members.length, escaped: false });
    for (const member of members) enemies.push(member);
  };

  const unlockedForms = (): FormKind[] => {
    if (level <= 1) return ["vee", "line"];
    if (level === 2) return ["vee", "line", "sweep", "divers"];
    return ["vee", "line", "sweep", "divers", "spiral"];
  };

  const nextForm = (): FormKind => {
    const options = unlockedForms().filter((kind) => kind !== lastForm);
    const kind = options[Math.floor(rng() * options.length)];
    lastForm = kind;
    return kind;
  };

  const formationGap = () => Math.max(4, 9 - level);

  const fireBullet = (x: number, y: number, angle: number, speed: number) => {
    if (bullets.length >= MAX_BULLETS) return;
    const distance = Math.hypot(x - shipX, y - shipY());
    // Never spawn a bullet on top of the ship, or close enough to be unfair.
    if (distance < safeSpawnDistance()) return;
    closestBulletSpawn = Math.min(closestBulletSpawn, distance);
    bullets.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, r: bulletRadius(), grazed: false });
    tally.add("bullet");
  };

  const aimAt = (x: number, y: number) => Math.atan2(shipY() - y, shipX - x);

  const enemyFire = (e: Enemy) => {
    if (e.y > height * 0.62 || !onScreen(e)) return;
    const speed = bulletSpeed();
    if (e.style === "down") fireBullet(e.x, e.y, Math.PI / 2, speed * 0.95);
    else if (e.style === "aimed") fireBullet(e.x, e.y, aimAt(e.x, e.y), speed);
    else if (e.style === "spread") {
      const angle = aimAt(e.x, e.y);
      const side = level >= 5 ? 2 : 1;
      for (let k = -side; k <= side; k++) fireBullet(e.x, e.y, angle + k * 0.28, speed * 0.92);
    }
  };

  const startBoss = () => {
    stage = "boss";
    stageClock = 0;
    const hp = Math.round((70 + (level - 1) * 34) * difficulty);
    boss = {
      level,
      name: BOSS_NAMES[Math.min(level - 1, BOSS_NAMES.length - 1)],
      hp,
      maxHp: hp,
      x: width / 2,
      y: -base() * 0.2,
      t: 0,
      enter: 0,
      beats: 0,
      spiral: 0,
      burstLeft: 0,
      burstTimer: 0,
      halfTimer: 0,
      hitFlash: 0,
      hitAt: damage,
    };
    bossesSpawned += 1;
    bossLog.push(`spawn L${level} ${time.toFixed(1)}`);
    tally.add("boss");
    fx.banner("BOSS", boss.name, palette.danger);
    feedbackQueue.push("boss");
  };

  // The boss sways across the top, a little wider each phase.
  const bossXAt = (b: Boss, t: number) => width / 2 + Math.sin(t * 0.55) * Math.min(width / 2 - bossRadius() - 20, 240) * (0.55 + bossPhase(b) * 0.12);

  const bossRadius = () => Math.max(40, Math.min(88, base() * 0.12));
  const bossPhase = (b: Boss) => (b.hp > b.maxHp * 0.66 ? 0 : b.hp > b.maxHp * 0.33 ? 1 : 2);

  const spiralVolley = (b: Boss) => {
    const arms = 5 + Math.min(4, b.level);
    const r = bossRadius();
    b.spiral += 0.26 + b.level * 0.04;
    for (let i = 0; i < arms; i++) {
      const angle = b.spiral + (i / arms) * Math.PI * 2;
      if (Math.sin(angle) < 0.2) continue;
      fireBullet(b.x + Math.cos(angle) * r * 0.8, b.y + Math.sin(angle) * r * 0.8, angle, bulletSpeed() * 0.72);
    }
  };

  const bossBeat = (b: Boss) => {
    b.beats += 1;
    const phase = bossPhase(b);
    const L = b.level;
    const speed = bulletSpeed();
    const r = bossRadius();
    const muzzleY = b.y + r * 0.7;
    if (phase === 0) {
      if (b.beats % 2 === 0) {
        const n = 5 + Math.min(4, L - 1);
        const spread = 0.95 + Math.min(0.35, L * 0.05);
        const centre = aimAt(b.x, muzzleY) + (rng() - 0.5) * 0.3;
        for (let i = 0; i < n; i++) fireBullet(b.x, muzzleY, centre - spread / 2 + (spread * i) / (n - 1), speed * 0.85);
      } else if (b.beats % 4 === 1) {
        b.burstLeft = 3 + Math.min(2, L - 1);
        b.burstTimer = 0;
      }
    } else if (phase === 1) {
      spiralVolley(b);
      // From level 2 the spiral also fires on the off beat.
      if (L >= 2) b.halfTimer = beatSeconds / 2;
      if (b.beats % 8 === 4) summonMinions(b);
    } else {
      if (b.beats % 4 === 0) {
        const n = 20 + L * 2;
        const gapAt = aimAt(b.x, b.y) + (rng() - 0.5) * 0.5;
        for (let i = 0; i < n; i++) {
          const angle = (i / n) * Math.PI * 2;
          const off = Math.abs(Math.atan2(Math.sin(angle - gapAt), Math.cos(angle - gapAt)));
          if (off < 0.34 || Math.sin(angle) < 0.1) continue;
          fireBullet(b.x + Math.cos(angle) * r * 0.8, b.y + Math.sin(angle) * r * 0.8, angle, speed * 0.78);
        }
      } else if (b.beats % 2 === 1) {
        b.burstLeft = 4 + Math.min(3, L - 1);
        b.burstTimer = 0;
      } else if (L >= 3) spiralVolley(b);
      if (b.beats % 8 === 6) summonMinions(b);
    }
  };

  const summonMinions = (b: Boss) => {
    groupId += 1;
    const group = groupId;
    const count = b.level >= 3 ? 3 : 2;
    let made = 0;
    for (let i = 0; i < count; i++) {
      const side = count === 2 ? (i === 0 ? -1 : 1) : i - 1;
      const hx = clamp(b.x / width + side * 0.22, 0.1, 0.9);
      if (enemies.length > 26) break;
      enemies.push(
        makeEnemy("minion", "divers", group, {
          ex: b.x / width,
          ey: b.y / height,
          hx,
          hy: 0.34,
          tIn: 0.9,
          tHold: 5,
          sway: 0.03,
          fireEvery: 4,
          fireOffset: i * 2,
        }),
      );
      made += 1;
    }
    groups.set(group, { left: made, escaped: true });
  };

  const dropPowerUp = (x: number, y: number, kind?: PowerKind) => {
    let chosen = kind;
    if (!chosen) {
      dropTurn += 1;
      const roll = rng();
      if (dropTurn === 1) chosen = "triple";
      else if (bullets.length > 14 && roll < 0.35) chosen = "bomb";
      else if (!shield && roll < 0.3) chosen = "shield";
      else if (roll < 0.62) chosen = "triple";
      else if (roll < 0.9) chosen = "rapid";
      else chosen = level >= 2 ? "bomb" : "shield";
    }
    powerUps.push({ x: clamp(x, 30, width - 30), y, kind: chosen, age: 0 });
    tally.add(chosen);
  };

  const bassBomb = () => {
    const y = shipY();
    for (const bullet of bullets) {
      fx.burst(bullet.x, bullet.y, palette.accent, 2, 60, 0);
      score += 5;
    }
    bullets = [];
    for (const e of enemies) if (onScreen(e)) hurtEnemy(e, 3, e.x, e.y);
    if (boss && boss.enter >= 1) hurtBoss(Math.ceil(boss.maxHp * 0.08), boss.x, boss.y);
    fx.ring(shipX, y, palette.accent, Math.max(width, height) * 0.9, 0.8);
    fx.ring(shipX, y, palette.primary, Math.max(width, height) * 0.6, 0.6);
    fx.kick(10);
    fx.banner("BASS BOMB", "Bullets cleared", palette.accent);
  };

  const awardKill = (e: Enemy) => {
    combo += 1;
    const points = e.kind === "diver" ? 120 : e.kind === "gunner" ? 80 : e.kind === "minion" ? 40 : 50;
    const gain = Math.round(points * multiplier());
    score += gain;
    fx.burst(e.x, e.y, e.color, 14, 190, 30);
    fx.ring(e.x, e.y, e.color, enemyRadius() * 2.4);
    fx.text(e.x, e.y, `+${gain}`, palette.text, 15);
    if (combo > 0 && combo % FEVER_COMBO === 0 && combo !== lastFeverCombo) {
      lastFeverCombo = combo;
      fever = FEVER_SECONDS;
      fx.banner("FEVER", "Double points, triple shot", palette.accent);
    }
    const group = groups.get(e.group);
    if (group) {
      group.left -= 1;
      if (group.left <= 0) {
        groups.delete(e.group);
        if (!group.escaped) {
          const bonus = Math.round(150 * level * (fever > 0 ? 2 : 1));
          score += bonus;
          fx.text(e.x, e.y - 26, `WAVE CLEAR +${bonus}`, palette.accent, 17);
          feedbackQueue.push("score");
          dropPowerUp(e.x, e.y);
        }
      }
    }
  };

  const hurtEnemy = (e: Enemy, amount: number, x: number, y: number) => {
    if (e.gone) return;
    e.hp -= amount;
    e.hitFlash = 1;
    if (e.hp <= 0) {
      e.gone = true;
      awardKill(e);
    } else fx.burst(x, y, palette.secondary, 4, 80, 0);
  };

  const hurtBoss = (amount: number, x: number, y: number) => {
    if (!boss) return;
    boss.hp -= amount;
    boss.hitFlash = 1;
    if (rng() < 0.3) fx.burst(x, y, palette.secondary, 3, 90, 0);
    if (boss.hp <= 0) defeatBoss();
  };

  const defeatBoss = () => {
    if (!boss) return;
    const b = boss;
    const flawless = damage === b.hitAt;
    const reward = Math.round(2500 * b.level * (flawless ? 2 : 1) * streakMultiplier() * (fever > 0 ? 2 : 1));
    score += reward;
    bossesDefeated += 1;
    bossLog.push(`down L${b.level} ${time.toFixed(1)}`);
    for (const bullet of bullets) fx.burst(bullet.x, bullet.y, palette.accent, 2, 50, 0);
    score += bullets.length * 10;
    bullets = [];
    for (let i = 0; i < 4; i++) fx.burst(b.x + (rng() - 0.5) * 60, b.y + (rng() - 0.5) * 60, i % 2 ? palette.danger : palette.accent, 30, 360, 60);
    fx.ring(b.x, b.y, palette.accent, bossRadius() * 5, 0.9);
    fx.ring(b.x, b.y, palette.danger, bossRadius() * 3, 0.6);
    fx.kick(14);
    fx.banner("BOSS DOWN", flawless ? `Flawless +${reward}` : `+${reward}`, palette.accent);
    feedbackQueue.push("win");
    dropPowerUp(b.x - 50, b.y, "shield");
    dropPowerUp(b.x + 50, b.y, level % 2 === 0 ? "rapid" : "triple");
    for (const e of enemies) if (e.kind === "minion" && !e.gone) hurtEnemy(e, 9, e.x, e.y);
    boss = null;
    stage = "clear";
    stageClock = 0;
  };

  const loseLife = () => {
    if (shield) {
      shield = false;
      invulnerable = 1;
      fx.ring(shipX, shipY(), palette.accent, 120);
      fx.text(shipX, shipY() - 44, "SHIELD", palette.accent);
      feedbackQueue.push("hurt");
      return;
    }
    lives -= 1;
    damage += 1;
    feedbackQueue.push("hurt");
    combo = 0;
    lastFeverCombo = 0;
    lastHitAt = time;
    invulnerable = 1.6;
    fx.kick(14);
    fx.burst(shipX, shipY(), palette.danger, 30, 280);
    fx.ring(shipX, shipY(), palette.danger, 120);
    if (lives <= 0) over = true;
  };

  const onBeat = () => {
    beatGlow = 1;
    if (stage === "waves") {
      const alive = enemies.some((e) => !e.gone && e.kind !== "minion");
      beatsToForm -= 1;
      if (!alive) beatsToForm = Math.min(beatsToForm, 0);
      if (beatsToForm <= 0 && stageClock < waveSeconds() - 2) {
        // The very first wave is always the friendly V.
        if (level === 1 && lastForm === null) {
          lastForm = "vee";
          spawnFormation("vee");
        } else spawnFormation(nextForm());
        if (level >= 4 && rng() < 0.4) spawnFormation(rng() < 0.5 ? "divers" : "sweep");
        beatsToForm = formationGap();
      }
    }
    if (boss && boss.enter >= 1) bossBeat(boss);
    for (const e of enemies) {
      if (e.gone || !onScreen(e)) continue;
      if (e.kind === "diver" || e.kind === "minion") {
        const a = e.age - e.delay;
        if (e.dive === 0 && a > e.tIn + 0.4 && e.y < height * 0.5 && (beatCount + e.fireOffset) % e.fireEvery === 0) {
          e.dive = 1;
          e.diveT = 0;
          e.diveX = clamp(shipX / width, 0.05, 0.95);
          e.hitFlash = 1;
        }
        continue;
      }
      if (e.fireEvery > 0 && e.charge <= 0 && (beatCount + e.fireOffset) % e.fireEvery === 0) e.charge = CHARGE_SECONDS;
    }
    beatCount += 1;
  };

  const inspect = (): BassBlasterDebug => ({
    level,
    stage,
    boss: boss ? { hp: boss.hp, maxHp: boss.maxHp, name: boss.name } : null,
    bossesSpawned,
    bossesDefeated,
    closestBulletSpawn,
    bossLog: [...bossLog],
    formations: { ...formations },
    shipX,
    shipY: shipY(),
    bullets: bullets.map((b) => ({ x: b.x, y: b.y })),
  });

  return {
    bloom: 0.45,
    inspect,

    resize(nextWidth, nextHeight) {
      const sx = nextWidth / Math.max(1, width);
      const sy = nextHeight / Math.max(1, height);
      width = nextWidth;
      height = nextHeight;
      shipX *= sx;
      targetX *= sx;
      shipPositionY *= sy;
      targetY *= sy;
      lastChoice *= sx;
      for (const item of [...shots, ...bullets, ...powerUps]) {
        item.x *= sx;
        item.y *= sy;
      }
      for (const e of enemies) {
        e.diveFromX *= sx;
        e.diveFromY *= sy;
      }
      if (boss) {
        boss.x *= sx;
        boss.y *= sy;
      }
    },

    autopilot() {
      const y = shipY();
      const hit = hitRadius();
      const vmax = maxSpeed();
      // Where every threat will be over the next second, sampled once.
      let count = 0;
      for (const b of bullets) {
        if (count >= MAX_TRACKED) break;
        if (b.vy <= 0 && b.y < y - 40) continue;
        if (Math.abs(b.y - y) > Math.abs(b.vy) * PLAN_STEP * PLAN_STEPS + 60) continue;
        for (let s = 0; s < PLAN_STEPS; s++) {
          const t = (s + 1) * PLAN_STEP;
          trackX[s * MAX_TRACKED + count] = b.x + b.vx * t;
          trackY[s * MAX_TRACKED + count] = b.y + b.vy * t;
        }
        trackR[count] = b.r + hit;
        count += 1;
      }
      for (const e of enemies) {
        if (count >= MAX_TRACKED) break;
        if (e.gone) continue;
        const r = enemyRadius() * e.size * 0.85 + hit;
        let near = false;
        for (let s = 0; s < PLAN_STEPS; s++) {
          predict(e, (s + 1) * PLAN_STEP, point);
          trackX[s * MAX_TRACKED + count] = point.x;
          trackY[s * MAX_TRACKED + count] = point.y;
          if (point.y > y - r - 40) near = true;
        }
        if (!near) continue;
        trackR[count] = r;
        count += 1;
      }

      // What it wants to line up under: the boss, else the lowest enemy.
      let aim = -1;
      let aimSlack = 0;
      if (boss && boss.enter >= 1) {
        // Lead the boss by the time a shot takes to reach it.
        aim = bossXAt(boss, boss.t + (y - boss.y) / (height * 1.55));
        aimSlack = bossRadius() * 0.5;
      }
      else {
        let best = -Infinity;
        for (const e of enemies) {
          if (e.gone || !onScreen(e) || e.y > y - shipSize() * 4) continue;
          const value = e.y + (e.dive === 1 ? height : 0) - Math.abs(e.x - shipX) * 0.4;
          if (value > best) {
            best = value;
            aim = e.x;
          }
        }
      }
      let pickup = -1;
      for (const p of powerUps) if (p.y > height * 0.3 && p.y < y + 10) pickup = p.x;

      lanes.length = 0;
      const margin = shipSize();
      for (let i = 0; i < PLAN_LANES; i++) lanes.push(margin + ((width - margin * 2) * i) / (PLAN_LANES - 1));
      lanes.push(shipX, lastChoice);
      if (aim >= 0) lanes.push(clamp(aim, margin, width - margin));
      if (pickup >= 0) lanes.push(clamp(pickup, margin, width - margin));

      const buffer = shipSize() * 1.4;
      // Pull toward targets the same way on a phone and a wide screen.
      const reach = Math.min(width * 0.4, 180);
      let bestX = shipX;
      let bestCost = Infinity;
      for (const lane of lanes) {
        let x = shipX;
        let danger = 0;
        for (let s = 0; s < PLAN_STEPS && danger < 5000; s++) {
          const v = clamp((lane - x) * 14, -vmax, vmax);
          x += v * PLAN_STEP;
          const weight = 1 - s / (PLAN_STEPS * 2);
          const row = s * MAX_TRACKED;
          for (let j = 0; j < count; j++) {
            const dx = trackX[row + j] - x;
            const dy = trackY[row + j] - y;
            const reach = trackR[j] + buffer;
            if (dx > reach || dx < -reach || dy > reach || dy < -reach) continue;
            const clear = Math.sqrt(dx * dx + dy * dy) - trackR[j];
            if (clear < 2) danger += 1000 * weight;
            else if (clear < buffer) danger += ((buffer - clear) / buffer) ** 2 * 12 * weight;
          }
        }
        let cost = danger + (Math.abs(lane - shipX) / width) * 1.5;
        if (aim >= 0) cost += Math.min(1, Math.max(0, Math.abs(lane - aim) - aimSlack) / reach) * (boss ? 9 : 6);
        if (pickup >= 0) cost += Math.min(1, Math.abs(lane - pickup) / reach) * 8;
        if (Math.abs(lane - width / 2) > width * 0.42) cost += 1.5;
        if (Math.abs(lane - lastChoice) < 4) cost -= 0.6;
        if (cost < bestCost) {
          bestCost = cost;
          bestX = lane;
        }
      }
      lastChoice = bestX;
      return pointerInput(bestX, y);
    },

    update(dt, input: FrameInput, audio: AudioFrame) {
      travel += dt * (0.05 + audio.energy * 0.12);
      beatGlow = Math.max(0, beatGlow - dt * 4);
      if (over) {
        fx.update(dt);
        return;
      }
      time += dt;
      stageClock += dt;
      invulnerable = Math.max(0, invulnerable - dt);
      tripleShot = Math.max(0, tripleShot - dt);
      rapidFire = Math.max(0, rapidFire - dt);
      fever = Math.max(0, fever - dt);

      // Steering: the ship chases the finger or mouse at a capped speed, and
      // the arrow keys push it directly.
      if (input.pointer && (input.pointer.down || input.pointer.moved)) {
        targetX = input.pointer.x;
        targetY = input.pointer.y;
      }
      let steer = 0;
      let vertical = 0;
      for (const code of input.held) {
        if (LEFT_KEYS.has(code)) steer -= 1;
        if (RIGHT_KEYS.has(code)) steer += 1;
        if (code === "ArrowUp" || code === "KeyW") vertical -= 1;
        if (code === "ArrowDown" || code === "KeyS") vertical += 1;
      }
      const previousX = shipX;
      const vmax = maxSpeed();
      const diagonal = steer && vertical ? Math.SQRT1_2 : 1;
      if (vertical) {
        shipPositionY += vertical * vmax * 0.9 * diagonal * dt;
        targetY = shipPositionY;
      } else shipPositionY += clamp((targetY - shipPositionY) * 14, -vmax, vmax) * dt;
      // Leave the upper stage to incoming formations; the lower half is yours.
      shipPositionY = clamp(shipPositionY, height * 0.52, height - shipSize() * 1.5);
      targetY = clamp(targetY, height * 0.52, height - shipSize() * 1.5);
      if (steer !== 0) {
        shipX += steer * vmax * 0.9 * diagonal * dt;
        targetX = shipX;
      } else shipX += clamp((targetX - shipX) * 14, -vmax, vmax) * dt;
      shipX = clamp(shipX, shipSize(), width - shipSize());
      shipTilt += (((shipX - previousX) / Math.max(dt, 0.001) / vmax) * 0.35 - shipTilt) * Math.min(1, dt * 10);

      fireTimer -= dt;
      if (fireTimer <= 0) {
        fireTimer = (rapidFire > 0 ? 0.085 : 0.15) - audio.treble * 0.02;
        const speed = -height * 1.55;
        const y = shipY() - shipSize();
        shots.push({ x: shipX, y, vx: 0, vy: speed });
        if (tripleShot > 0 || fever > 0) {
          shots.push({ x: shipX - 8, y: y + 6, vx: -height * 0.2, vy: speed });
          shots.push({ x: shipX + 8, y: y + 6, vx: height * 0.2, vy: speed });
        }
      }

      beatSeconds = clamp(audio.beatPeriod / 1000, 0.3, 1);
      if (audio.beat) onBeat();

      if (stage === "waves" && stageClock >= waveSeconds()) startBoss();
      if (stage === "clear" && stageClock >= CLEAR_SECONDS) {
        level += 1;
        stage = "waves";
        stageClock = 0;
        lastForm = null;
        fx.banner(`LEVEL ${level}`, LEVEL_NOTES[Math.min(level, LEVEL_NOTES.length - 1)], palette.primary);
        feedbackQueue.push("level");
        spawnFormation(nextForm());
        beatsToForm = formationGap();
      }

      for (const e of enemies) {
        if (e.gone) continue;
        e.age += dt;
        e.hitFlash = Math.max(0, e.hitFlash - dt * 5);
        e.spin += dt * (1.5 + audio.bass * 3);
        if (e.dive === 1) {
          e.diveT += dt;
          if (e.diveT >= DIVE_WARN_SECONDS) {
            e.dive = 2;
            e.diveT = 0;
            e.diveFromX = e.x;
            e.diveFromY = e.y;
          }
        } else if (e.dive === 2) e.diveT += dt;
        predict(e, 0, point);
        e.x = point.x;
        e.y = point.y;
        if (e.charge > 0) {
          e.charge -= dt;
          if (e.charge <= 0) enemyFire(e);
        }
        const a = e.age - e.delay;
        const offBottom = e.y > height + enemyRadius() * 2;
        const offSide = e.form === "sweep" && a > 0.5 && (e.x < -enemyRadius() * 3 || e.x > width + enemyRadius() * 3);
        const offSpiral = e.form === "spiral" && a > e.tIn + e.tHold && (e.x < -60 || e.x > width + 60 || e.y < -60 || e.y > height + 60);
        if (offBottom || offSide || offSpiral) {
          e.gone = true;
          const group = groups.get(e.group);
          if (group) {
            group.escaped = true;
            group.left -= 1;
            if (group.left <= 0) groups.delete(e.group);
          }
        }
      }

      if (boss) {
        const b = boss;
        b.t += dt;
        b.hitFlash = Math.max(0, b.hitFlash - dt * 6);
        const r = bossRadius();
        const homeY = Math.max(height * 0.2, 96 + r);
        b.enter = Math.min(1, b.enter + dt / 2.2);
        b.x = bossXAt(b, b.t) * b.enter + (width / 2) * (1 - b.enter);
        b.y = -r + (homeY + r) * easeOut(b.enter);
        if (b.halfTimer > 0) {
          b.halfTimer -= dt;
          if (b.halfTimer <= 0 && bossPhase(b) === 1) spiralVolley(b);
        }
        if (b.burstLeft > 0) {
          b.burstTimer -= dt;
          if (b.burstTimer <= 0) {
            b.burstLeft -= 1;
            b.burstTimer = 0.13;
            fireBullet(b.x, b.y + r * 0.7, aimAt(b.x, b.y + r * 0.7), bulletSpeed() * 1.05);
          }
        }
      }

      for (const shot of shots) {
        shot.x += shot.vx * dt;
        shot.y += shot.vy * dt;
      }
      for (const bullet of bullets) {
        bullet.x += bullet.vx * dt;
        bullet.y += bullet.vy * dt;
      }
      for (const power of powerUps) {
        power.age += dt;
        power.y += height * 0.13 * dt;
        power.x += Math.sin(power.age * 2.4) * 30 * dt;
      }

      // Player shots against the boss and the formations.
      const r0 = enemyRadius();
      for (const shot of shots) {
        if (shot.y < -30) continue;
        if (boss) {
          const r = bossRadius();
          const dx = shot.x - boss.x;
          const dy = shot.y - boss.y;
          if (dx * dx + dy * dy < r * r) {
            shot.y = -100;
            if (boss.enter >= 1) hurtBoss(1, shot.x, shot.y + 100);
            if (!boss) break;
            continue;
          }
        }
        for (const e of enemies) {
          if (e.gone) continue;
          const r = r0 * e.size;
          const dx = shot.x - e.x;
          const dy = shot.y - e.y;
          if (dx * dx + dy * dy < r * r) {
            hurtEnemy(e, 1, shot.x, shot.y);
            shot.y = -100;
            break;
          }
        }
      }

      const sy = shipY();
      const hit = hitRadius();
      if (invulnerable <= 0) {
        for (const e of enemies) {
          if (e.gone) continue;
          const r = r0 * e.size * 0.85 + hit;
          if (Math.abs(e.x - shipX) < r && Math.abs(e.y - sy) < r && Math.hypot(e.x - shipX, e.y - sy) < r) {
            hurtEnemy(e, 9, e.x, e.y);
            loseLife();
            break;
          }
        }
      }
      for (const bullet of bullets) {
        const dx = bullet.x - shipX;
        const dy = bullet.y - sy;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < bullet.r + hit && invulnerable <= 0) {
          bullet.y = height + 100;
          loseLife();
          if (over) break;
          continue;
        }
        // Grazing: a bullet that slips past close is worth a little.
        if (!bullet.grazed && d < bullet.r + hit + shipSize() * 1.1 && invulnerable <= 0) {
          bullet.grazed = true;
          score += Math.round(10 * multiplier());
          if (time - lastGraze > 1.2) {
            lastGraze = time;
            fx.text(shipX, sy - 40, "GRAZE", palette.accent, 14);
            feedbackQueue.push("score");
          }
        }
      }
      for (const power of powerUps) {
        if (Math.hypot(power.x - shipX, power.y - sy) > shipSize() * 1.9) continue;
        power.y = height + 100;
        if (power.kind === "shield") shield = true;
        else if (power.kind === "triple") tripleShot = 10;
        else if (power.kind === "rapid") rapidFire = 10;
        else bassBomb();
        score += 100;
        if (power.kind !== "bomb") fx.text(shipX, sy - 48, POWER_NAME[power.kind], palette.accent, 17);
        fx.burst(shipX, sy, palette.accent, 18, 170);
        fx.ring(shipX, sy, palette.accent, 70);
        feedbackQueue.push("score");
      }

      if (!env.reducedMotion) fx.trail(shipX + (rng() - 0.5) * 6, sy + shipSize() * 0.8, palette.secondary, 0, height * 0.5);

      enemies = enemies.filter((e) => !e.gone);
      shots = shots.filter((shot) => shot.y > -30 && shot.x > -30 && shot.x < width + 30);
      bullets = bullets.filter((b) => b.y < height + 30 && b.y > -40 && b.x > -30 && b.x < width + 30);
      powerUps = powerUps.filter((power) => power.y < height + 30);
      fx.update(dt);
    },

    draw(ctx, audio) {
      ctx.fillStyle = palette.backgroundDeep;
      ctx.fillRect(0, 0, width, height);
      if (nebula) {
        const size = Math.max(width, height) * 1.4;
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(nebula as CanvasImageSource, (width - size) / 2, -((travel * 40) % size) * 0.02 - size * 0.15, size, size);
        ctx.restore();
      }
      drawStars(ctx, farStars, width, height, time, palette.text, { x: 0, y: travel * 0.6 }, audio.treble);

      const planetR = base() * 0.22;
      const planetX = width * 0.76;
      const planetY = height * 0.34 + Math.sin(time * 0.1) * 6;
      drawGlow(ctx, art, palette.primary, planetX, planetY, planetR * 2, 0.22 + audio.pulse * 0.1);
      ctx.save();
      ctx.globalAlpha = 0.55;
      drawCoverDisc(ctx, art, palette, planetX, planetY, planetR, time * 0.02);
      ctx.restore();
      const terminator = ctx.createRadialGradient(planetX - planetR * 0.5, planetY - planetR * 0.5, planetR * 0.2, planetX, planetY, planetR * 1.05);
      terminator.addColorStop(0, "rgba(0,0,0,0.1)");
      terminator.addColorStop(0.7, "rgba(0,0,0,0.45)");
      terminator.addColorStop(1, withAlpha(palette.backgroundDeep, 0.9));
      ctx.fillStyle = terminator;
      ctx.beginPath();
      ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2);
      ctx.fill();
      drawStars(ctx, nearStars, width, height, time, palette.accent, { x: 0, y: travel * 1.8 }, audio.treble * 0.5);

      ctx.save();
      fx.applyShake(ctx);

      // A low spectrum under the ship, thin and faded so bullets read over it.
      const bars = audio.spectrum.length;
      const barWidth = width / bars;
      const floor = height * 0.1;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const fade = ctx.createLinearGradient(0, height - floor, 0, height);
      fade.addColorStop(0, withAlpha(palette.primary, 0));
      fade.addColorStop(1, withAlpha(palette.primary, 0.16));
      ctx.fillStyle = fade;
      for (let i = 0; i < bars; i++) {
        const h = audio.spectrum[i] * floor;
        ctx.fillRect(i * barWidth + barWidth * 0.3, height - h, barWidth * 0.4, h);
      }
      ctx.restore();

      // Diver warnings: a column that fills in before the drop.
      for (const e of enemies) {
        if (e.dive !== 1) continue;
        const p = Math.min(1, e.diveT / DIVE_WARN_SECONDS);
        const x = e.diveX * width;
        ctx.save();
        ctx.strokeStyle = withAlpha(palette.danger, 0.18 + p * 0.4);
        ctx.lineWidth = 2 + p * 3;
        ctx.setLineDash([10, 10]);
        ctx.lineDashOffset = -time * 60;
        ctx.beginPath();
        ctx.moveTo(x, e.y + enemyRadius());
        ctx.lineTo(x, height);
        ctx.stroke();
        ctx.restore();
      }

      for (const power of powerUps) {
        const pulse = 1 + Math.sin(power.age * 6) * 0.08;
        const s = Math.max(15, shipSize() * 0.8) * pulse;
        const color = power.kind === "shield" ? palette.accent : power.kind === "bomb" ? palette.danger : power.kind === "rapid" ? palette.secondary : palette.primary;
        drawGlow(ctx, art, color, power.x, power.y, s * 2.4, 0.7);
        ctx.save();
        ctx.translate(power.x, power.y);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
          if (i === 0) ctx.moveTo(Math.cos(a) * s, Math.sin(a) * s);
          else ctx.lineTo(Math.cos(a) * s, Math.sin(a) * s);
        }
        ctx.closePath();
        ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.85);
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.font = `800 ${Math.round(s * 1.05)}px Poppins, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(POWER_LABEL[power.kind], 0, 1);
        ctx.restore();
      }

      const r0 = enemyRadius();
      for (const e of enemies) {
        if (!onScreen(e)) continue;
        const r = r0 * e.size;
        const pulse = 1 + audio.pulse * 0.08;
        drawVinyl(ctx, art, palette, e.x, e.y, r * pulse, e.spin, 0);
        ctx.save();
        ctx.strokeStyle = e.hitFlash > 0 ? "#fff" : e.color;
        ctx.lineWidth = e.kind === "grunt" ? 2 : 3;
        ctx.beginPath();
        ctx.arc(e.x, e.y, r * pulse + 2, 0, Math.PI * 2);
        ctx.stroke();
        if (e.kind === "diver" || e.kind === "minion") {
          // A downward chevron marks the ones that dive.
          ctx.fillStyle = e.dive === 1 && Math.floor(e.diveT * 10) % 2 === 0 ? "#fff" : palette.danger;
          ctx.beginPath();
          ctx.moveTo(e.x - r * 0.55, e.y + r * 0.95);
          ctx.lineTo(e.x, e.y + r * 1.45);
          ctx.lineTo(e.x + r * 0.55, e.y + r * 0.95);
          ctx.closePath();
          ctx.fill();
        }
        if (e.charge > 0) {
          // Gunners flash a closing ring before each shot.
          const p = 1 - e.charge / CHARGE_SECONDS;
          ctx.strokeStyle = withAlpha(palette.danger, 0.4 + p * 0.6);
          ctx.lineWidth = 2.5;
          ctx.beginPath();
          ctx.arc(e.x, e.y, r * (2.2 - p * 1.1), 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = withAlpha(palette.danger, 0.3 + p * 0.6);
          ctx.beginPath();
          ctx.arc(e.x, e.y, r * 0.3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (boss) drawBoss(ctx, boss, audio);

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.lineCap = "round";
      ctx.strokeStyle = withAlpha(palette.accent, 0.85);
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (const shot of shots) {
        ctx.moveTo(shot.x, shot.y);
        ctx.lineTo(shot.x - shot.vx * 0.014, shot.y - shot.vy * 0.014);
      }
      ctx.stroke();
      ctx.restore();

      // Enemy bullets: a dark rim, a hot body and a white core so they read
      // against anything behind them.
      for (const b of bullets) drawGlow(ctx, art, palette.danger, b.x, b.y, b.r * 3, 0.55);
      ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.9);
      ctx.beginPath();
      for (const b of bullets) {
        ctx.moveTo(b.x + b.r + 2, b.y);
        ctx.arc(b.x, b.y, b.r + 2, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.fillStyle = palette.danger;
      ctx.beginPath();
      for (const b of bullets) {
        ctx.moveTo(b.x + b.r, b.y);
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      for (const b of bullets) {
        ctx.moveTo(b.x + b.r * 0.45, b.y);
        ctx.arc(b.x, b.y, b.r * 0.45, 0, Math.PI * 2);
      }
      ctx.fill();

      drawShip(ctx, audio);

      fx.draw(ctx);
      ctx.restore();
      if (boss && !env.quiet) drawBossBar(ctx, boss);
      const streak = streakMultiplier();
      if (streak > 1 && !env.quiet) {
        ctx.save();
        ctx.font = "700 12px Poppins, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = withAlpha(palette.accent, 0.9);
        ctx.fillText(`NO HIT x${streak}`, 14, height - 12);
        ctx.restore();
      }
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, fever > 0 ? palette.accent : palette.primary, audio.pulse * 0.25 + fx.flash);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe(): GameProbe {
      let threats = boss ? 1 : 0;
      for (const e of enemies) if (onScreen(e)) threats += 1;
      threats += bullets.length;
      return { threats, pickups: powerUps.length, spawned: tally.spawned, damage, intensity: heat(), boss: boss !== null, mission: null };
    },

    status(): GameStatus {
      return { score, lives, maxLives: MAX_LIVES, combo, meter: null, over, level, fever: fever > 0 };
    },
  };

  function drawShip(ctx: CanvasRenderingContext2D, audio: AudioFrame) {
    const size = shipSize();
    const y = shipY();
    ctx.save();
    ctx.globalAlpha = invulnerable > 0 && Math.floor(invulnerable * 12) % 2 === 0 ? 0.35 : 1;
    const flame = size * (0.7 + audio.bass * 0.8 + audio.pulse * 0.3);
    drawGlow(ctx, art, palette.secondary, shipX, y + size * 0.9, flame * 0.8, 0.8);
    ctx.translate(shipX, y);
    ctx.rotate(clamp(shipTilt, -0.35, 0.35));
    const hull = ctx.createLinearGradient(0, -size, 0, size);
    hull.addColorStop(0, "#fff");
    hull.addColorStop(0.35, palette.primary);
    hull.addColorStop(1, palette.backgroundDeep);
    ctx.fillStyle = hull;
    ctx.beginPath();
    ctx.moveTo(0, -size * 1.1);
    ctx.lineTo(size * 0.35, -size * 0.1);
    ctx.lineTo(size * 1.0, size * 0.55);
    ctx.lineTo(size * 0.3, size * 0.45);
    ctx.lineTo(0, size * 0.75);
    ctx.lineTo(-size * 0.3, size * 0.45);
    ctx.lineTo(-size * 1.0, size * 0.55);
    ctx.lineTo(-size * 0.35, -size * 0.1);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = withAlpha(palette.accent, 0.9);
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
    // The hitbox is only the bright core, drawn so players learn it.
    ctx.save();
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = palette.danger;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(shipX, y, hitRadius() + 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    if (shield) {
      ctx.save();
      ctx.strokeStyle = withAlpha(palette.accent, 0.65 + Math.sin(time * 8) * 0.2);
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(shipX, y, size * 1.8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    // Power up timers as short arcs under the ship.
    let slot = 0;
    for (const [left, color] of [
      [tripleShot, palette.primary],
      [rapidFire, palette.secondary],
    ] as const) {
      if (left <= 0) continue;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      const rr = size * (1.35 + slot * 0.3);
      ctx.arc(shipX, y, rr, Math.PI * 0.2, Math.PI * 0.2 + Math.PI * 0.6 * Math.min(1, left / 10));
      ctx.stroke();
      ctx.restore();
      slot += 1;
    }
  }

  function drawBoss(ctx: CanvasRenderingContext2D, b: Boss, audio: AudioFrame) {
    const r = bossRadius();
    const phase = bossPhase(b);
    const charge = b.enter >= 1 ? clamp(1 - audio.nextBeatIn / Math.max(1, audio.beatPeriod), 0, 1) : 0;
    const core = phase === 0 ? palette.secondary : phase === 1 ? palette.accent : palette.danger;
    drawGlow(ctx, art, core, b.x, b.y, r * 2.2, 0.35 + charge * 0.25 + beatGlow * 0.1);
    drawVinyl(ctx, art, palette, b.x, b.y, r, b.t * (1 + phase * 0.6), 0);
    ctx.save();
    ctx.translate(b.x, b.y);
    // Armour plates spin faster each phase.
    ctx.rotate(-b.t * (0.6 + phase * 0.5));
    ctx.strokeStyle = core;
    ctx.lineWidth = Math.max(4, r * 0.1);
    ctx.lineCap = "round";
    const plates = 6;
    ctx.beginPath();
    for (let i = 0; i < plates; i++) {
      const a = (i / plates) * Math.PI * 2;
      ctx.moveTo(Math.cos(a) * r * 1.14, Math.sin(a) * r * 1.14);
      ctx.arc(0, 0, r * 1.14, a, a + (Math.PI * 2) / plates - 0.28);
    }
    ctx.stroke();
    if (b.hitFlash > 0) {
      // Hits flash a thin white edge on the plates.
      ctx.strokeStyle = withAlpha("rgb(255, 255, 255)", b.hitFlash * 0.7);
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    ctx.fillStyle = withAlpha(core, 0.55 + charge * 0.45);
    ctx.beginPath();
    ctx.arc(b.x, b.y, r * (0.16 + charge * 0.08), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawBossBar(ctx: CanvasRenderingContext2D, b: Boss) {
    const barWidth = Math.min(width * 0.62, 460);
    const x = (width - barWidth) / 2;
    const y = height < 600 ? 84 : 100;
    const fill = clamp(b.hp / b.maxHp, 0, 1);
    ctx.save();
    ctx.globalAlpha = Math.min(1, b.enter * 3);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(x - 3, y - 3, barWidth + 6, 14);
    ctx.fillStyle = withAlpha(palette.danger, 0.3);
    ctx.fillRect(x, y, barWidth, 8);
    ctx.fillStyle = palette.danger;
    ctx.fillRect(x, y, barWidth * fill, 8);
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    for (const mark of [1 / 3, 2 / 3]) ctx.fillRect(x + barWidth * mark - 1, y - 2, 2, 12);
    ctx.restore();
    glowText(ctx, b.name, width / 2, y + 24, 13, palette.danger, Math.min(1, b.enter * 3) * 0.95);
  }
}
